import { useState, useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { Button, Card, CardSection } from '@trafiflow/shared-ui'
import { GitBranch, X } from 'lucide-react'
import { useFunnels } from '../../hooks'
import { FunnelListHeader } from './FunnelListHeader'
import { FunnelFilters } from './FunnelFilters'
import { FunnelTable } from './FunnelTable'
import { FunnelPricingTable } from './FunnelPricingTable'
import { EmptyState } from '../shared/EmptyState'
import { Pagination } from '../shared/Pagination'
import { ConfirmModal } from '../shared/ConfirmModal'
import { DialogPortal } from '../shared/DialogPortal'
import { DEFAULT_FILTERS } from '../../types'
import { inputClass, textareaClass, labelClass } from '../../styles/class-utils'

export function FunnelListPage() {
  const {
    funnels,
    allFunnels,
    loading,
    error,
    filters,
    viewMode,
    isCreateModalOpen,
    setIsCreateModalOpen,
    setViewMode,
    updateFilter,
    resetFilters,
    handleCreate,
    handleDelete,
    handleDuplicate,
  } = useFunnels()

  const [newFunnel, setNewFunnel] = useState({
    name: '',
    description: '',
    priority: 0,
    isActive: true,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [duplicateTarget, setDuplicateTarget] = useState<{ id: string; name: string } | null>(null)
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([])
  const [bulkDuplicateIds, setBulkDuplicateIds] = useState<string[]>([])
  const [createError, setCreateError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const ITEMS_PER_PAGE = 10

  const paginatedFunnels = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return funnels.slice(start, start + ITEMS_PER_PAGE)
  }, [funnels, currentPage])

  const handleDeleteClick = (id: string) => {
    const funnel = allFunnels.find((f) => f.id === id)
    setDeleteTarget({ id, name: funnel?.name || 'this funnel' })
  }

  const handleDuplicateClick = (id: string) => {
    const funnel = allFunnels.find((f) => f.id === id)
    setDuplicateTarget({ id, name: funnel?.name || 'this funnel' })
  }

  const hasActiveFilters =
    filters.search !== DEFAULT_FILTERS.search ||
    filters.status.length > 0 ||
    filters.funnelType.length > 0 ||
    filters.currency.length > 0 ||
    filters.trafficType.length > 0 ||
    filters.productType.length > 0 ||
    filters.countries.length > 0 ||
    filters.dateFrom !== DEFAULT_FILTERS.dateFrom ||
    filters.dateTo !== DEFAULT_FILTERS.dateTo

  const handleCreateSubmit = async () => {
    if (!newFunnel.name.trim() || creating) return
    setCreating(true)
    setCreateError(null)
    try {
      await handleCreate(newFunnel)
      setIsCreateModalOpen(false)
      setNewFunnel({ name: '', description: '', priority: 0, isActive: true })
      setCreateError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create funnel. Please try again.'
      setCreateError(message)
    } finally {
      setCreating(false)
    }
  }

  if (loading && allFunnels.length === 0) {
    return (
      <div className="funnels-theme flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="funnels-theme">
        <Card>
          <CardSection>
            <div className="text-center py-12">
              <p className="text-sm text-fn-error">
                {error}
              </p>
            </div>
          </CardSection>
        </Card>
      </div>
    )
  }

  return (
    <div className="funnels-theme animate-fade-in">
      <FunnelListHeader
        totalCount={allFunnels.length}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onCreateNew={() => { setCreateError(null); setIsCreateModalOpen(true) }}
      />

      {allFunnels.length === 0 ? (
        <Card>
          <CardSection>
            <EmptyState
              icon={<GitBranch className="w-6 h-6 text-fn-text-muted" />}
              title="No funnels yet"
              description="Create your first funnel to start optimizing conversions"
              actionLabel="Create Funnel"
              onAction={() => setIsCreateModalOpen(true)}
            />
          </CardSection>
        </Card>
      ) : (
        <>
          <FunnelFilters
            filters={filters}
            onFilterChange={updateFilter}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
          />

          {funnels.length === 0 ? (
            <Card>
              <CardSection>
                <EmptyState
                  icon={<GitBranch className="w-6 h-6 text-fn-text-muted" />}
                  title="No funnels match your filters"
                  description="Try adjusting your search or filter criteria"
                />
              </CardSection>
            </Card>
          ) : (
            <Card>
              <CardSection>
                {viewMode === 'flow' ? (
                  <FunnelTable
                    funnels={paginatedFunnels}
                    onDelete={handleDeleteClick}
                    onDuplicate={handleDuplicateClick}
                    onBulkDelete={(ids) => setBulkDeleteIds(ids)}
                    onBulkDuplicate={(ids) => setBulkDuplicateIds(ids)}
                  />
                ) : (
                  <FunnelPricingTable funnels={paginatedFunnels} />
                )}
                <div className="px-4 pb-3">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={funnels.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </CardSection>
            </Card>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await handleDelete(deleteTarget.id)
          setDeleteTarget(null)
        }}
        title="Delete Funnel"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />

      <ConfirmModal
        isOpen={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onConfirm={async () => {
          if (duplicateTarget) await handleDuplicate(duplicateTarget.id)
          setDuplicateTarget(null)
        }}
        title="Duplicate Funnel"
        description={`Duplicate "${duplicateTarget?.name}"? This will create a copy with "(Copy)" appended to the name.`}
        confirmLabel="Duplicate"
        variant="primary"
      />

      <ConfirmModal
        isOpen={bulkDeleteIds.length > 0}
        onClose={() => setBulkDeleteIds([])}
        onConfirm={async () => {
          for (const id of bulkDeleteIds) await handleDelete(id)
          setBulkDeleteIds([])
        }}
        title="Delete Selected Funnels"
        description={`Are you sure you want to delete ${bulkDeleteIds.length} funnel${bulkDeleteIds.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmLabel="Delete All"
        variant="danger"
      />

      <ConfirmModal
        isOpen={bulkDuplicateIds.length > 0}
        onClose={() => setBulkDuplicateIds([])}
        onConfirm={async () => {
          for (const id of bulkDuplicateIds) await handleDuplicate(id)
          setBulkDuplicateIds([])
        }}
        title="Duplicate Selected Funnels"
        description={`Duplicate ${bulkDuplicateIds.length} funnel${bulkDuplicateIds.length !== 1 ? 's' : ''}? Each will get a copy with "(Copy)" appended.`}
        confirmLabel="Duplicate All"
        variant="primary"
      />

      {isCreateModalOpen && (
        <DialogPortal>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50 animate-fade-in"
            onClick={() => { if (!creating) { setIsCreateModalOpen(false); setCreateError(null) } }}
          />
          <div className="relative z-10 w-full max-w-lg bg-fn-card rounded-lg shadow-fn-modal border border-fn-border animate-scale-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-fn-border">
              <h2 className="text-lg font-semibold text-fn-text">Create New Funnel</h2>
              <button
                onClick={() => { if (!creating) { setIsCreateModalOpen(false); setCreateError(null) } }}
                className="p-1 rounded hover:bg-fn-hover transition-colors"
              >
                <X className="w-5 h-5 text-fn-text-muted" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {createError && (
                <div className="px-3 py-2 text-sm text-red-600 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-md">
                  {createError}
                </div>
              )}
              <div>
                <label className={labelClass}>
                  Funnel Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Enter funnel name"
                  value={newFunnel.name}
                  onChange={(e) =>
                    setNewFunnel((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  Description
                </label>
                <textarea
                  className={textareaClass}
                  rows={3}
                  placeholder="Optional description"
                  value={newFunnel.description}
                  onChange={(e) =>
                    setNewFunnel((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  Priority
                </label>
                <input
                  type="number"
                  className={inputClass}
                  placeholder="0"
                  value={newFunnel.priority}
                  onChange={(e) =>
                    setNewFunnel((prev) => ({
                      ...prev,
                      priority: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="funnel-active"
                  className="w-3.5 h-3.5 rounded border-tf-border text-primary focus:ring-primary"
                  checked={newFunnel.isActive}
                  onChange={(e) =>
                    setNewFunnel((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                />
                <label htmlFor="funnel-active" className="text-sm text-fn-text">
                  Active
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-fn-border">
              <Button variant="secondary" onClick={() => { setIsCreateModalOpen(false); setCreateError(null) }} disabled={creating}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateSubmit}
                disabled={!newFunnel.name.trim() || creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
        </DialogPortal>
      )}
    </div>
  )
}
